import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import Lenis from 'lenis';
import { Download, Mail, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [pdfs, setPdfs] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ first_name: '', email: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Initialize smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    fetchPdfs();
  }, []);

  const fetchPdfs = async () => {
    try {
      const response = await axios.get(`${API}/pdfs`);
      setPdfs(response.data);
    } catch (error) {
      console.error('Error fetching PDFs:', error);
    }
  };

  const handleDownloadClick = (pdf) => {
    setSelectedPdf(pdf);
    setShowEmailModal(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailData.first_name || !emailData.email) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/pdfs/${selectedPdf.id}/download`,
        emailData,
        { responseType: 'blob' }
      );

      // Create blob and download file
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedPdf.filename || 'download.pdf';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success('PDF downloaded! Check your downloads folder.');
      setShowEmailModal(false);
      setEmailData({ first_name: '', email: '' });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to download PDF';
      toast.error(`Download failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const groupedPdfs = pdfs.reduce((acc, pdf) => {
    if (!acc[pdf.category]) acc[pdf.category] = [];
    acc[pdf.category].push(pdf);
    return acc;
  }, {});

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative min-h-screen flex items-center justify-center px-6 md:px-12 py-20 overflow-hidden"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1757447247468-fe0962f638fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZGVzayUyMHNldHVwJTIwbm90ZWJvb2slMjBjb2ZmZWUlMjBjYWxtfGVufDB8fHx8MTc2NzIyNjM3Mnww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_4a1e8a2c-d558-43d3-a645-acf842c0f069/artifacts/jxrv1t5f_500px-invigoreight-pic.png"
              alt="InvigorEight Logo"
              className="h-24 md:h-32 mb-8 logo-glow"
              data-testid="logo-image"
            />
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-stone-900 mb-6 leading-tight" data-testid="hero-headline">
              Building money, habits, and health—so your life compounds instead of burns out.
            </h1>
            
            <p className="text-lg text-stone-600 mb-8 leading-relaxed" data-testid="hero-subheadline">
              Simple systems for long-term stability. No hype. No shortcuts.
            </p>
            
            <Button
              onClick={() => document.getElementById('resources').scrollIntoView({ behavior: 'smooth' })}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-full shadow-md"
              data-testid="hero-cta-button"
            >
              Get the Free InvigorEight Guide
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>

          {/* Right: Image */}
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden md:block"
          >
            <img
              src="https://images.unsplash.com/photo-1601128688653-7dc405e3ac4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB3cml0aW5nJTIwam91cm5hbCUyMGNhbG0lMjBzdW5saWdodHxlbnwwfHx8fDE3NjcyMjYzNzN8MA&ixlib=rb-4.1.0&q=85"
              alt="Person writing calmly"
              className="rounded-lg shadow-xl"
            />
          </motion.div>
        </div>
      </motion.section>

      {/* About Section */}
      <section className="py-20 px-6 md:px-12 bg-stone-50" data-testid="about-section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-light text-stone-900 mb-6" data-testid="about-heading">
              Built on Discipline, Not Hype
            </h2>
            <p className="text-base md:text-lg text-stone-700 leading-relaxed max-w-3xl mx-auto" data-testid="about-description">
              InvigorEight isn't about hustling harder or getting rich overnight. We focus on calm, disciplined systems 
              that help people build their money, lifestyle, and health—one layer at a time. Our approach is systematic, 
              sustainable, and designed for long-term compounding results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Free Resources Section */}
      <section id="resources" className="py-20 px-6 md:px-12" data-testid="resources-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-light text-stone-900 mb-4" data-testid="resources-heading">
              Free InvigorEight Resources
            </h2>
            <p className="text-base md:text-lg text-stone-600" data-testid="resources-subheading">
              Download our free guides and start building systems that compound.
            </p>
          </motion.div>

          {Object.keys(groupedPdfs).length === 0 && (
            <p className="text-center text-stone-500">No resources available yet. Check back soon!</p>
          )}

          {Object.entries(groupedPdfs).map(([category, categoryPdfs], idx) => (
            <div key={category} className="mb-16">
              <h3 className="text-2xl font-semibold text-stone-900 mb-8 flex items-center gap-3" data-testid={`category-${category.toLowerCase()}-heading`}>
                <span className="h-1 w-12 bg-red-600"></span>
                {category}
              </h3>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryPdfs.map((pdf, pdfIdx) => (
                  <motion.div
                    key={pdf.id}
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: pdfIdx * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white border border-stone-200 rounded-lg p-6 shadow-sm card-hover"
                    data-testid={`pdf-card-${pdf.id}`}
                  >
                    <div className="mb-4">
                      <div className="inline-block px-3 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-full mb-3">
                        {pdf.category}
                      </div>
                      <h4 className="text-xl font-semibold text-stone-900 mb-2" data-testid={`pdf-title-${pdf.id}`}>
                        {pdf.title}
                      </h4>
                      <p className="text-sm text-stone-600 leading-relaxed" data-testid={`pdf-description-${pdf.id}`}>
                        {pdf.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-stone-100">
                      <span className="text-xs text-stone-500 flex items-center gap-1">
                        <Download className="h-3 w-3" />
                        {pdf.download_count} downloads
                      </span>
                      <Button
                        onClick={() => handleDownloadClick(pdf)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm"
                        data-testid={`download-button-${pdf.id}`}
                      >
                        Download
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Future Signal Section */}
      <section className="py-16 px-6 md:px-12 bg-stone-100" data-testid="future-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="text-base text-stone-600 italic" data-testid="future-description">
              In the future, InvigorEight will include courses, planners, and memberships across money, lifestyle, and health. 
              Subscribe now to get early access when we launch.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_4a1e8a2c-d558-43d3-a645-acf842c0f069/artifacts/jxrv1t5f_500px-invigoreight-pic.png"
            alt="InvigorEight Logo"
            className="h-12 mx-auto mb-4 opacity-80"
          />
          <p className="text-sm text-stone-400">
            &copy; {new Date().getFullYear()} InvigorEight. Building systems that compound.
          </p>
        </div>
      </footer>

      {/* Email Capture Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md" data-testid="email-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light" data-testid="modal-title">Get Your Free Guide</DialogTitle>
            <DialogDescription data-testid="modal-description">
              Enter your details to download <strong>{selectedPdf?.title}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEmailSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="first_name" className="text-stone-700">First Name</Label>
              <Input
                id="first_name"
                type="text"
                placeholder="John"
                value={emailData.first_name}
                onChange={(e) => setEmailData({ ...emailData, first_name: e.target.value })}
                className="mt-1"
                required
                data-testid="modal-first-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-stone-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={emailData.email}
                onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                className="mt-1"
                required
                data-testid="modal-email-input"
              />
            </div>
            
            <p className="text-xs text-stone-500 italic" data-testid="modal-privacy-text">
              We don't spam. We build systems.
            </p>
            
            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white py-6 rounded-md"
              disabled={loading}
              data-testid="modal-submit-button"
            >
              {loading ? 'Downloading...' : 'Send Me the Guide'}
              <Mail className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;